import { ApolloQueryResult } from '@apollo/client/index.js';
import { GetMeQuery, useGetMeQuery } from '@pm/graphql';
import * as Sentry from '@sentry/react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { createContext, PropsWithChildren, use, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ENVIRONMENT } from '../constants';
import { LoadingPage } from '../pages/LoadingPage';

type ProfileContextType = {
  profile: GetMeQuery['me'];
  refetch: () => Promise<ApolloQueryResult<GetMeQuery>>;
  loading: boolean;
};

export const ProfileContext = createContext<ProfileContextType | undefined>(
  undefined,
);

export const useProfileContext = () => use(ProfileContext);

export const ProfileProvider = ({ children }: PropsWithChildren) => {
  const LDClient = useLDClient();
  const { i18n } = useTranslation();
  const { data, refetch, loading, error } = useGetMeQuery();

  const me = data?.me;

  useEffect(() => {
    if (me) {
      // set our language
      i18n.changeLanguage(me.locale);

      // set sentry user
      Sentry.getCurrentScope().setUser({ id: me.id });

      // set launch darkly user
      LDClient?.identify({
        kind: 'user',
        key: me.id,
        custom: {
          email: ENVIRONMENT === 'production' ? undefined : me.email,
        },
      });
    }
  }, [LDClient, i18n, me]);

  // set segment user
  useEffect(() => {
    if (me && window.analytics && window.analytics.identify) {
      window.analytics.identify(me.id, {
        role: me.role.code,
        email: me.email,
      });
    }
  }, [me]);

  if (!me) {
    return loading ? <LoadingPage /> : null;
  }

  return (
    <ProfileContext
      value={{
        profile: data.me,
        refetch,
        loading: loading || (!data && !error),
      }}
    >
      {children}
    </ProfileContext>
  );
};
